import { useEffect, useRef, useState } from "react";
import {
  BellOff,
  FileText,
  Headphones,
  Mic,
  MicOff,
  Phone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOff,
  Video,
  VideoOff,
  X,
} from "lucide-react";
import { useWhatsAppCall } from "../../context/WhatsAppCallContext";

const STATE_LABELS = {
  idle: "Iniciando…",
  calling: "Chamando…",
  ringing: "Tocando…",
  connecting: "Conectando…",
  active: "Em ligação",
  waiting_room: "Aguardando…",
};

function formatPhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  const match = digits.match(/^55(\d{2})(\d{4,5})(\d{4})$/);
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : digits;
}

function callTitle(call) {
  return call.contactName || formatPhone(call.peerPhone) || "Número não identificado";
}

const CallTimer = ({ since }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, []);

  const seconds = Math.max(0, Math.floor(now / 1000 - since));
  return (
    <span className="tabular-nums">
      {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
    </span>
  );
};

// Fica montado fora das telas: a ligação continua ao trocar de conversa ou de aba do CRM.
const WhatsAppCallPanel = () => {
  const {
    activeCall,
    incomingCalls,
    missedCalls,
    returnCall,
    dismissMissed,
    pending,
    busy,
    muted,
    listenOnly,
    audioLive,
    answerCall,
    rejectCall,
    hangup,
    toggleMute,
    resumeAudio,
    videoSupported,
    cameraOn,
    videoLive,
    remoteVideo,
    toggleCamera,
    acceptVideo,
    attachVideoElements,
    canMakeCalls,
    canReceiveCalls,
    ringing,
    silenceRinging,
    transcriptionEnabled,
    transcribing,
    transcriptionFailed,
    startTranscription,
  } = useWhatsAppCall();

  const remoteRef = useRef(null);
  const localRef = useRef(null);
  const showVideo = Boolean(activeCall && (activeCall.video || cameraOn || videoLive));

  // Entrega ao módulo de vídeo onde desenhar o cliente e a própria câmera.
  useEffect(() => {
    attachVideoElements(showVideo ? { canvas: remoteRef.current, local: localRef.current } : null);
  }, [showVideo, attachVideoElements]);

  if (!activeCall && !incomingCalls.length && !missedCalls.length && !pending) return null;

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 flex ${
        showVideo ? "w-[28rem]" : "w-80"
      } max-w-[calc(100vw-2rem)] flex-col gap-3`}
    >
      {missedCalls.map((missed) => (
        <div
          key={missed.id}
          className="rounded-xl border border-red-200 bg-white p-4 shadow-xl dark:border-red-800 dark:bg-gray-800"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 text-xs font-medium text-red-600 dark:text-red-400">
              <PhoneMissed className="h-4 w-4" />
              Ligação perdida
              {missed.startedAt && (
                <span className="text-gray-500 dark:text-gray-400">
                  ·{" "}
                  {new Date(missed.startedAt * 1000).toLocaleTimeString("pt-BR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              )}
            </div>
            <button
              onClick={() => dismissMissed(missed.id)}
              title="Dispensar"
              className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-1 truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
            {callTitle(missed)}
          </div>
          {missed.contactName && missed.peerPhone && (
            <div className="text-xs text-gray-500 dark:text-gray-400">{formatPhone(missed.peerPhone)}</div>
          )}
          {canMakeCalls && (
            <button
              onClick={() => returnCall(missed)}
              disabled={busy}
              title={busy ? "Encerre a ligação atual para retornar" : "Ligar de volta pelo WhatsApp"}
              className="mt-3 w-full rounded-lg bg-green-500 px-3 py-2 text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Phone className="mr-2 inline h-4 w-4" />
              Retornar ligação
            </button>
          )}
        </div>
      ))}

      {incomingCalls.map((call) => (
        <div
          key={call.id}
          className="rounded-xl border border-green-200 bg-white p-4 shadow-xl dark:border-green-800 dark:bg-gray-800"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs font-medium text-green-600 dark:text-green-400">
              {call.video ? (
                <Video className="h-4 w-4 animate-pulse" />
              ) : (
                <PhoneIncoming className="h-4 w-4 animate-pulse" />
              )}
              {call.video ? "Videochamada de WhatsApp" : "Ligação de WhatsApp"}
            </div>
            {/* Para só o som; o aviso continua com Atender/Recusar. */}
            {ringing && (
              <button
                onClick={silenceRinging}
                title="Silenciar o toque (o aviso continua)"
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                <BellOff className="h-3.5 w-3.5" />
                Silenciar
              </button>
            )}
          </div>
          <div className="mt-1 truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
            {callTitle(call)}
          </div>
          {call.contactName && call.peerPhone && (
            <div className="text-xs text-gray-500 dark:text-gray-400">{formatPhone(call.peerPhone)}</div>
          )}
          {/* Sem Atender: número em coexistência com a Cloud API (o WhatsApp
              recusa o atendimento por aparelho vinculado) ou o CRM já está em
              outra ligação (uma por vez). O celular do escritório segue tocando. */}
          {call.answerable === false && (
            <div className="mt-2 rounded-lg bg-yellow-50 px-3 py-2 text-xs text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200">
              {call.unanswerableReason === "busy" ? (
                <>
                  Já existe uma ligação em andamento no CRM. Atenda esta no{" "}
                  <b>celular do escritório</b>.
                </>
              ) : (
                <>
                  Este número só permite atender no <b>celular do escritório</b>.
                </>
              )}
            </div>
          )}
          <div className="mt-3 flex gap-2">
            {call.answerable !== false && canReceiveCalls && (
              <button
                onClick={() => answerCall(call.id)}
                disabled={busy}
                title={busy ? "Encerre a ligação atual para atender" : "Atender pelo computador"}
                className="flex-1 rounded-lg bg-green-500 px-3 py-2 text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Phone className="mr-2 inline h-4 w-4" />
                Atender
              </button>
            )}
            <button
              onClick={() => rejectCall(call.id)}
              className="flex-1 rounded-lg bg-red-500 px-3 py-2 text-white transition-colors hover:bg-red-600"
            >
              <PhoneOff className="mr-2 inline h-4 w-4" />
              Recusar
            </button>
          </div>
        </div>
      ))}

      {pending && !activeCall && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-600 shadow-xl dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
          Iniciando ligação…
        </div>
      )}

      {activeCall && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate font-semibold text-gray-900 dark:text-gray-100">
                {callTitle(activeCall)}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {STATE_LABELS[activeCall.state] || activeCall.state}
                {listenOnly && " · só ouvindo (sem microfone)"}
              </div>
              {transcribing && (
                <div className="mt-0.5 flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                  Transcrevendo · avise o cliente
                </div>
              )}
            </div>
            {activeCall.state === "active" && activeCall.answeredAt && (
              <div className="text-lg text-gray-900 dark:text-gray-100">
                <CallTimer since={activeCall.answeredAt} />
              </div>
            )}
          </div>

          {showVideo && (
            <div className="relative mt-3 aspect-video overflow-hidden rounded-lg bg-black">
              <canvas ref={remoteRef} className="h-full w-full object-contain" />
              {!remoteVideo && (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-300">
                  {activeCall.peerVideo ? "Recebendo vídeo…" : "Câmera do cliente desligada"}
                </div>
              )}
              {/* Própria câmera, espelhada como num espelho. */}
              <video
                ref={localRef}
                autoPlay
                muted
                playsInline
                className={`absolute bottom-2 right-2 w-28 -scale-x-100 rounded-md border border-white/40 bg-gray-900 ${
                  cameraOn ? "" : "hidden"
                }`}
              />
            </div>
          )}

          {activeCall.videoUpgradeRequested && !cameraOn && (
            <div className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
              O cliente quer passar para vídeo.
              <button
                onClick={acceptVideo}
                className="rounded-md bg-blue-600 px-2 py-1 text-white hover:bg-blue-700"
              >
                Aceitar
              </button>
            </div>
          )}

          <div className="mt-3 flex gap-2">
            {!audioLive ? (
              <button
                onClick={resumeAudio}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <Headphones className="mr-2 inline h-4 w-4" />
                Reativar áudio
              </button>
            ) : (
              !listenOnly && (
                <button
                  onClick={toggleMute}
                  className={`flex-1 rounded-lg border px-3 py-2 transition-colors ${
                    muted
                      ? "border-yellow-400 bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                  }`}
                >
                  {muted ? <MicOff className="mr-2 inline h-4 w-4" /> : <Mic className="mr-2 inline h-4 w-4" />}
                  {muted ? "Reativar" : "Mudo"}
                </button>
              )
            )}
            {videoSupported && activeCall.state !== "calling" && (
              <button
                onClick={toggleCamera}
                title={cameraOn ? "Desligar câmera" : "Ligar câmera (vira videochamada)"}
                className={`flex-1 rounded-lg border px-3 py-2 transition-colors ${
                  cameraOn
                    ? "border-green-400 bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                }`}
              >
                {cameraOn ? <Video className="mr-2 inline h-4 w-4" /> : <VideoOff className="mr-2 inline h-4 w-4" />}
                Câmera
              </button>
            )}
            <button
              onClick={hangup}
              className="flex-1 rounded-lg bg-red-500 px-3 py-2 text-white transition-colors hover:bg-red-600"
            >
              <PhoneOff className="mr-2 inline h-4 w-4" />
              Encerrar
            </button>
          </div>

          {/* A transcrição começa sozinha quando a ligação fica ativa; o botão
              só aparece se ela não conseguiu começar, para tentar de novo. */}
          {transcriptionEnabled && transcriptionFailed && !transcribing && audioLive && (
            <button
              onClick={() => startTranscription()}
              title="Grava os dois lados, transcreve e salva um resumo nas notas da conversa"
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <FileText className="h-3.5 w-3.5" />
              Transcrição não iniciou — tentar de novo
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default WhatsAppCallPanel;
